import { useState, useRef, useEffect, useCallback } from 'react'
import { VOICE_CONFIG, isSpeechRecognitionSupported, getSpeechRecognition, sanitizeText } from '../utils/voiceConfig.js'

function VoiceRecorder({ onTranscript, language = 'es', disabled = false, style = {} }) {
  const [status, setStatus]           = useState('idle')
  const [isRecording, setIsRecording] = useState(false)
  const [lang, setLang]               = useState(language)
  const [secs, setSecs]               = useState(60)
  const [liveText, setLiveText]       = useState('')
  const [denied, setDenied]           = useState(false)
  const [svcError, setSvcError]       = useState(false)

  const recRef  = useRef(null)
  const t1Ref   = useRef(null)
  const t2Ref   = useRef(null)
  const liveRef = useRef(false)   // isRecording without stale closure
  const textRef = useRef('')      // accumulated transcript

  const isSupported = isSpeechRecognitionSupported()
  // Installed PWA (standalone): Android Chrome's webkitSpeechRecognition often
  // fails here with 'not-allowed' even when the mic IS permitted — the speech
  // service isn't reachable in standalone. We tailor the guidance accordingly.
  const isStandalone = typeof window !== 'undefined' &&
    ((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
     window.navigator.standalone === true)

  useEffect(() => () => { clearTimeout(t1Ref.current); clearInterval(t2Ref.current); recRef.current?.abort() }, [])

  // ── Stop ──────────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    if (!liveRef.current) return
    liveRef.current = false
    setIsRecording(false)
    clearTimeout(t1Ref.current)
    clearInterval(t2Ref.current)
    setSecs(60)
    recRef.current?.stop()

    const text = sanitizeText(textRef.current)
    textRef.current = ''
    setLiveText('')

    if (text.trim()) {
      onTranscript(text)
      setStatus('complete')
      setTimeout(() => setStatus('idle'), 2000)
    } else {
      setStatus('idle')
    }
  }, [onTranscript])

  // ── Record ─────────────────────────────────────────────────────────────────

  const record = useCallback(() => {
    if (!isSupported || disabled || liveRef.current) return
    const SR  = getSpeechRecognition()
    const rec = new SR()
    rec.continuous = false; rec.interimResults = true
    rec.lang = VOICE_CONFIG.languages[lang]?.code || 'es-ES'

    rec.onresult = (e) => {
      let fin = '', tmp = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript
        if (e.results[i].isFinal) fin += t; else tmp += t
      }
      if (fin) textRef.current += (textRef.current ? ' ' : '') + fin.trim()
      setLiveText(textRef.current + (tmp ? ' ' + tmp : ''))
    }

    rec.onerror = (e) => {
      if (e.error === 'aborted') return
      // Real mic-permission denial → recovery card.
      if (e.error === 'not-allowed') {
        liveRef.current = false; setIsRecording(false); setDenied(true); setStatus('idle')
        clearTimeout(t1Ref.current); clearInterval(t2Ref.current)
        return
      }
      // The speech SERVICE isn't available (offline, or unsupported PWA/WebView
      // context). NOT a mic-permission problem — say so honestly.
      if (e.error === 'service-not-allowed') {
        liveRef.current = false; setIsRecording(false); setSvcError(true); setStatus('idle')
        clearTimeout(t1Ref.current); clearInterval(t2Ref.current)
        return
      }
      // for network / no-speech: just stop silently, text already accumulated
      stop()
    }

    rec.onend = () => { if (liveRef.current) try { rec.start() } catch (_) { stop() } }

    recRef.current  = rec
    liveRef.current = true
    textRef.current = ''
    setIsRecording(true); setStatus('listening'); setSecs(60); setLiveText('')

    t2Ref.current = setInterval(() => setSecs(p => { if (p <= 1) { stop(); return 0 } return p - 1 }), 1000)
    t1Ref.current = setTimeout(stop, 60000)
    rec.start()
  }, [isSupported, disabled, lang, stop])

  // ── Tap handler ─────────────────────────────────────────────────────────
  // IMPORTANT: do NOT pre-acquire the mic with getUserMedia. webkitSpeechRecognition
  // grabs the mic itself; a getUserMedia preflight (even when it then releases the
  // tracks) races with start() on Android Chrome and makes it throw 'not-allowed'
  // even after the user has granted permission. start() handles the OS prompt on
  // its own, inside this user gesture, so we just call it directly.

  const tap = useCallback(() => {
    if (disabled) return
    if (liveRef.current) { stop(); return }
    setDenied(false)
    setSvcError(false)
    record()
  }, [disabled, stop, record])

  if (!isSupported) return null
  // In the installed PWA (standalone) Android's speech service isn't available,
  // so the button can only ever fail. Hide it entirely — the device keyboard's
  // own microphone is the way to dictate here.
  if (isStandalone) return null

  // ── Mic blocked — short recovery card ─────────────────────────────────────

  if (denied) {
    return (
      <div style={{ marginTop: '10px', background: '#fff8f0', border: '1.5px solid #e65100', borderRadius: '12px', padding: '12px 14px', ...style }}>
        <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#bf360c' }}>
          🎙️ Dictado por voz no disponible
        </p>
        <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#5d4037', lineHeight: '1.5' }}>
          {isStandalone
            ? 'En la app instalada el dictado del navegador no está disponible (aunque el micrófono esté permitido). Dicta directamente con el micrófono de tu teclado: toca el texto y pulsa 🎤.'
            : 'Permite el micrófono para este sitio y pulsa Reintentar. También puedes dictar con el micrófono de tu teclado: toca el texto y pulsa 🎤.'}
        </p>
        <button
          onClick={tap}
          style={{ width: '100%', padding: '9px', background: '#e65100', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
        >
          🔄 Reintentar
        </button>
      </div>
    )
  }

  // ── Speech service unavailable (offline / unsupported context) ────────────
  if (svcError) {
    return (
      <div style={{ marginTop: '10px', background: '#f4f8fa', border: '1.5px solid #5B8FA8', borderRadius: '12px', padding: '12px 14px', ...style }}>
        <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#2D5A6E' }}>
          🎙️ Dictado no disponible ahora
        </p>
        <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#5d4037', lineHeight: '1.5' }}>
          El reconocimiento de voz necesita conexión a internet. Comprueba tu conexión y pulsa Reintentar.
        </p>
        <button
          onClick={tap}
          style={{ width: '100%', padding: '9px', background: '#5B8FA8', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
        >
          🔄 Reintentar
        </button>
      </div>
    )
  }

  // ── Normal mic button ──────────────────────────────────────────────────────

  return (
    <div style={{ marginTop: '8px', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={tap}
          disabled={disabled}
          title={disabled ? 'Función Premium' : isRecording ? 'Detener' : 'Dictar por voz'}
          style={{
            width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `${isRecording ? 3 : 2}px solid ${isRecording ? '#E74C3C' : disabled ? '#ccc' : '#8B7355'}`,
            background: isRecording ? '#FFE5E5' : disabled ? '#f5f5f5' : '#FFF',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: '18px',
            boxShadow: isRecording ? '0 0 14px rgba(231,76,60,.35)' : 'none',
            transition: 'all .2s',
          }}
        >
          {isRecording ? '⏹️' : '🎙️'}
        </button>

        {!isRecording && (
          <select value={lang} onChange={e => setLang(e.target.value)} disabled={disabled}
            style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '12px', background: disabled ? '#f5f5f5' : '#FFF', cursor: disabled ? 'not-allowed' : 'pointer' }}>
            {Object.entries(VOICE_CONFIG.languages).map(([c, l]) => (
              <option key={c} value={c}>{l.flag} {l.name}</option>
            ))}
          </select>
        )}

        <span style={{ fontSize: '12px', fontWeight: '500', color: status === 'listening' ? '#27AE60' : status === 'complete' ? '#27AE60' : '#8B7355' }}>
          {status === 'listening' ? `Escuchando… ${secs}s` : status === 'complete' ? '✓ Texto añadido' : disabled ? 'Función Premium' : 'Presiona para hablar'}
        </span>
      </div>

      {isRecording && liveText && (
        <div style={{ marginTop: '8px', padding: '8px 12px', background: '#f9f6f2', border: '1px solid #e0d8cc', borderRadius: '8px', fontSize: '13px', color: '#555', fontStyle: 'italic', lineHeight: '1.5' }}>
          "{liveText}"
        </div>
      )}
    </div>
  )
}

export default VoiceRecorder
